import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { shopStore } from "../../store/Shop";
import HandImage from "../../assets/images/hand.png";
import { CURRENT_TEMP_DEMO_GENDER } from "../../utils/constants";
import "./style.scss";

// Анонимный демо-пользователь должен сперва выбрать пол (ChooseYourGenderModal),
// и только потом запускать shop-demo. Иначе анимация крутится на фоне gender-
// попапа — пользователь видит затемнение и пальчик до того, как ответил на
// первый экран. Авторизованных юзеров (cookie token) не блокируем.
const getCookieValue = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
};
const isGenderResolved = () => {
  const token = getCookieValue("token");
  if (token && token !== "undefined") return true;
  try {
    return Boolean(localStorage.getItem(CURRENT_TEMP_DEMO_GENDER));
  } catch (e) {
    return true;
  }
};

const measure = (el) => {
  const rect = el.getBoundingClientRect();
  const handLeft = rect.left + rect.width / 2 - 8;
  const handTop = rect.top + 16;
  const bubbleLeft = Math.max(
    12,
    Math.min(rect.left + rect.width / 2 - 140, window.innerWidth - 290)
  );
  const bubbleTop = Math.max(12, handTop - 130);
  return { handLeft, handTop, bubbleLeft, bubbleTop };
};

export default observer(function InGameShopDemo() {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const [pos, setPos] = useState(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (shopStore.isShopFingerDemoFinished) return () => {};

    const SELECTOR = ".main-swiper-wrapper__bottom-icons__shop .base-icon";

    const remeasure = () => {
      if (!mountedRef.current) return;
      const el = document.querySelector(SELECTOR);
      if (el) setPos(measure(el));
    };

    let attempts = 0;
    let genderWaitAttempts = 0;
    const tick = () => {
      if (!mountedRef.current) return;
      // Ждём пока юзер не закроет gender-попап (см. isGenderResolved).
      // До этого момента демо стартовать нельзя — затемнение перекроет
      // gender-выбор.
      if (!isGenderResolved()) {
        genderWaitAttempts++;
        if (genderWaitAttempts < 600) {
          // до 60 сек ожидания
          timerRef.current = setTimeout(tick, 100);
        }
        return;
      }
      const el = document.querySelector(SELECTOR);
      if (el) {
        setPos(measure(el));
        setActive(true);
        return;
      }
      attempts++;
      if (attempts < 60) {
        timerRef.current = setTimeout(tick, 100);
      }
    };
    tick();

    // Layout может «доехать» уже после первого замера (картинка карточки
    // догружается → высота меняется → иконка сдвигается). Перемериваем
    // на любой сдвиг: scroll/resize окна + ResizeObserver на body, плюс
    // window.load (картинки) и rAF в течение первой секунды.
    window.addEventListener("scroll", remeasure, true);
    window.addEventListener("resize", remeasure);
    window.addEventListener("load", remeasure);

    let bodyObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      bodyObserver = new ResizeObserver(remeasure);
      bodyObserver.observe(document.body);
    }

    // Контрольный sweep: перемеряем каждые 200ms в первую секунду —
    // покрывает медленные сети, где image onload может не сработать
    // вовремя или layout «дёргается».
    const sweepTimers = [200, 400, 600, 800, 1000, 1500, 2500].map((d) =>
      setTimeout(remeasure, d)
    );

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      sweepTimers.forEach(clearTimeout);
      window.removeEventListener("scroll", remeasure, true);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("load", remeasure);
      if (bodyObserver) bodyObserver.disconnect();
    };
  }, []);

  // 07.10: подсказка больше не перехватывает нажатия. Раньше полноэкранное
  // затемнение ловило первый тап и только закрывалось — значок анкеты рядом с
  // магазином «не нажимался» (в инкогнито подсказка показывается каждый раз).
  // Теперь слой прозрачен для тапов (pointer-events: none в стилях), а любой тап
  // по странице закрывает подсказку и доходит до того, на что нажали.
  useEffect(() => {
    if (!active) return undefined;
    const onAnyTap = () => {
      shopStore.markShopFingerDemoFinished();
      setActive(false);
    };
    document.addEventListener("pointerdown", onAnyTap, true);
    return () => document.removeEventListener("pointerdown", onAnyTap, true);
  }, [active]);

  if (!active || !pos || shopStore.isShopFingerDemoFinished) return null;

  const dismiss = () => {
    shopStore.markShopFingerDemoFinished();
    setActive(false);
  };

  return (
    <>
      <div className="ingame-shop-demo__backdrop" onClick={dismiss} />
      <div
        className="ingame-shop-demo__bubble"
        style={{ left: pos.bubbleLeft, top: pos.bubbleTop }}
        onClick={dismiss}
      >
        {t("demonstration.content.shop_ingame")}
      </div>
      <div
        className="ingame-shop-demo__hand"
        style={{ left: pos.handLeft, top: pos.handTop }}
        onClick={dismiss}
      >
        <img src={HandImage} alt="" />
      </div>
    </>
  );
});
